import * as React from 'react';
import { type GlobalPanelEntityType, type StackedPanel } from '@/store/panel-stack-store';
import { usePanelStackUrl } from '@/lib/hooks/use-panel-stack-url';
import { useLegacyPanelRedirect } from '@/lib/hooks/use-legacy-panel-redirect';

export interface GlobalDetailPanelContextValue {
  /** Current panel stack (bottom to top) */
  stack: StackedPanel[];
  /** Open a new panel (pushes to stack) */
  openPanel: (type: GlobalPanelEntityType, id: string) => void;
  /** Close the topmost panel */
  closeTopPanel: () => void;
  /** Close all panels at and above a given index */
  closePanelAt: (index: number) => void;
  /** Close all panels */
  closeAllPanels: () => void;
  /** Navigate to a different entity within a panel (replaces ID at index) */
  navigateWithinPanel: (index: number, newId: string) => void;
  /** Check if a specific panel is in the stack */
  isPanelOpen: (type: GlobalPanelEntityType, id: string) => boolean;
}

const GlobalDetailPanelContext = React.createContext<GlobalDetailPanelContextValue | null>(null);

/**
 * Provider for global detail panels with URL synchronization.
 * No Suspense boundary needed - TanStack Router's useLocation() is synchronous.
 */
export function GlobalDetailPanelProvider({ children }: { children: React.ReactNode }) {
  // Handle legacy ?id= URL pattern redirect to new ?panelStack= format
  useLegacyPanelRedirect();

  const { stack, openPanel, closeTopPanel, closePanelAt, closeAllPanels, navigateInPanel } =
    usePanelStackUrl();

  const isPanelOpen = React.useCallback(
    (type: GlobalPanelEntityType, id: string) => {
      return stack.some((p) => p.type === type && p.id === id);
    },
    [stack],
  );

  const value = React.useMemo<GlobalDetailPanelContextValue>(
    () => ({
      stack,
      openPanel,
      closeTopPanel,
      closePanelAt,
      closeAllPanels,
      navigateWithinPanel: navigateInPanel,
      isPanelOpen,
    }),
    [stack, openPanel, closeTopPanel, closePanelAt, closeAllPanels, navigateInPanel, isPanelOpen],
  );

  return (
    <GlobalDetailPanelContext.Provider value={value}>{children}</GlobalDetailPanelContext.Provider>
  );
}

export function useGlobalDetailPanelContext() {
  const context = React.useContext(GlobalDetailPanelContext);
  if (!context) {
    throw new Error('useGlobalDetailPanelContext must be used within a GlobalDetailPanelProvider');
  }
  return context;
}
